import { type FC } from "react";

import { LoadingSpinner } from "widgets";

import { type INavPanelCategory } from "../core/navigation-panel/NavigationPanel.js";
import { type IRouteNavItemProps } from "../core/navigation-panel/RouteNavItem.js";
import ContentBrowserChartBars from "./ContentBrowserChartBars.js";
import ContentBrowserChartInfo from "./ContentBrowserChartInfo.js";
import { type IRsqlComparisonExpr } from "@/app-utils/rsql.ts";
import { type ListingMapEntry, type OrganismsInfo } from "@/graphql.ts";
import { GROUP_BY_ATTRIBUTE_CREATION_TIME } from "@/settings/settings.js";

export interface IContentBrowserChartProps {
  isLoadingListing: boolean;
  isLoadingInfo?: boolean; // optional because not needed in User group admin page
  listing: ListingMapEntry[];
  organismsInfo: OrganismsInfo;
  selectedEntry: ListingMapEntry;
  selectedContentBrowserFeature: INavPanelCategory;
  selectedContentBrowserAttribute: IRouteNavItemProps;
  userGroupAdminContext?: boolean;
  changeGroupByAttributeFilter: (entry: ListingMapEntry) => void;
  changeStatisticsFilter?: (columnField: string, filter: IRsqlComparisonExpr) => void; // optional because not needed in User group admin page
  getBarTooltip?: (entry: ListingMapEntry) => string;
  handleEditAttribute?: (entry: ListingMapEntry) => void;
  handleDeleteAttribute?: (entry: ListingMapEntry) => void;
  editAttributeDisabledTooltip?: (entry: ListingMapEntry) => string; // callback to get the disabled tooltip depending on the entry
  deleteAttributeDisabledTooltip?: (entry: ListingMapEntry) => string; // callback to get the disabled tooltip depending on the entry
  totalCount: number; // for calculating the size of the chart bars
}

const ContentBrowserChart: FC<IContentBrowserChartProps> = (props) => {
  return props.isLoadingListing ? (
    <LoadingSpinner inCenter />
  ) : (
    <div className="content-browser-chart-view" style={{ height: "100%" }}>
      <div className="content-browser-chart-view-scrollable">
        <div className="content-browser-chart-view-col content-browser-info-text">
          {props.userGroupAdminContext ? (
            props.selectedContentBrowserAttribute?.eventKey === GROUP_BY_ATTRIBUTE_CREATION_TIME ? (
              <div>
                <p>
                  This chart shows a distribution of how long ago genomes (across all user groups) have been loaded into
                  the database.
                </p>
              </div>
            ) : (
              <div>
                <p>
                  Every user in the system is a member of the Public user group. Users and content may be associated
                  with additional user groups to allow for controlled sharing among a subset of users.
                </p>
              </div>
            )
          ) : props.isLoadingInfo ? (
            <LoadingSpinner inCenter />
          ) : (
            <ContentBrowserChartInfo
              selectedEntry={props.selectedEntry}
              selectedContentBrowserAttribute={props.selectedContentBrowserAttribute}
              organismsInfo={props.organismsInfo}
              listing={props.listing}
              changeStatisticsFilter={props.changeStatisticsFilter}
            />
          )}
        </div>
        <div className="content-browser-chart-view-col content-browser-chart-panel">
          <ContentBrowserChartBars
            data={props.listing}
            changeSelection={props.changeGroupByAttributeFilter}
            selectedEntry={props.selectedEntry}
            selectedContentBrowserFeature={props.selectedContentBrowserFeature}
            selectedContentBrowserAttribute={props.selectedContentBrowserAttribute}
            getBarTooltip={props.getBarTooltip}
            handleEdit={props.handleEditAttribute}
            handleDelete={props.handleDeleteAttribute}
            editDisabledTooltip={props.editAttributeDisabledTooltip}
            deleteDisabledTooltip={props.deleteAttributeDisabledTooltip}
            totalCount={props.totalCount}
          />
        </div>
      </div>
    </div>
  );
};

export default ContentBrowserChart;
